import React, {
  useEffect, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { animated, useSpring } from 'react-spring';
import { Field, Form, Formik } from 'formik';
import styled from '@emotion/styled';
import { GoogleLogin } from '@react-oauth/google';
import * as Yup from 'yup';
import { useTheme } from 'emotion-theming';
import { useLocation } from 'react-router-dom';
import { HiEye, HiEyeOff } from 'react-icons/hi';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';

import Button from '../../../../../shared/_TBComponents/Button';
import Checkmark from '../../../../../shared/_TBComponents/Checkmark';
import getAllUrlParams from '../../../../../shared/utils/getAllUrlParams';
import encode from '../../../../../shared/utils/encodeToUrl';
import Link from '../../../../../shared/_TBComponents/Link';
import Spinner from '../../../../../shared/_TBComponents/Spinner';
import Text from '../../../../../shared/_TBComponents/Text';
import useLogin from '../../hooks/useLogin';

const DividerContainer = styled('div')`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin-bottom: 10px;
`;

const FieldError = styled('div')`
  color: #ff6600;
  font-size: 14px;
  margin-top: 10px;
`;

const FormGroup = styled('div')`
  .divider-line {
    border-top: 2px solid rgb(232,232,232);
    display: flex;
    margin-bottom: 20px;
    margin-left: 10px;
    margin-right: 10px;
    margin-top: 20px;
    width: 100%;
  }
  .easy-join {
    margin-bottom: 15px;
    margin-top: 30px;
  }
  z-index: 1;
  @media screen and (max-width: 600px) {
    a {
      font-size: 11px;
    }
  }
`;

const PasswordFieldWrapper = styled('div')`
  position: relative;
`;

const PasswordToggleButton = styled('button')`
  align-items: center;
  background-color: transparent;
  border: none;
  color: #666;
  cursor: pointer;
  display: flex;
  font-size: 13px;
  justify-content: center;
  padding: 8px;
  pointer-events: auto;
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);

  &:focus {
    outline: none;
  }

  &:hover {
    color: #333;
  }
`;

export const SpinnerContainer = styled('div')`
  display: flex;
  flex: 1;
  height: 100%;
  justify-content: center;
  margin-left: -54px;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 0;
`;

const SSOFieldError = styled('div')`
  color: #ff6600;
  font-size: 14px;
  margin-bottom: 10px;
`;

const PasswordSchema = Yup.object().shape({
  username: Yup.string()
    .email('Invalid Email Address')
    .required('Email Required'),
  password: Yup.string()
    .required('Password Required'),
});

const EmailSchema = Yup.object().shape({
  username: Yup.string()
    .email('Invalid Email Address')
    .required('Email Required'),
});

const LoginFormSSO = ({ flags }) => {
  const isPreLoginLoading = useSelector((state) => state.auth.ui.isPreLoginLoading);
  const ssoData = useSelector((state) => state.auth.data.ssoData);
  const [googleLoginWidth, setGoogleLoginWidth] = useState('322');
  const [isNextButtonClicked, setIsNextButtonClicked] = useState(false);
  const [passwordButtonClicked, setPasswordButtonClicked] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const location = useLocation();
  const {
    googleError,
    handleGoogleAuth,
    handleLogin,
    handleLoginSSO,
    handlePreLogin,
    isAuthenticated,
    isLoginLoading,
    isSSOLoginLoading,
    loginError,
    ssoLoginError,
  } = useLogin();

  useEffect(() => {
    const urlParams = getAllUrlParams(window.location.href, false);
    const values = {
      client_id: process.env.CLIENT_ID,
      client_secret: process.env.CLIENT_SECRET,
      grant_type: 'sso_token',
      token: urlParams.token,
    };
    handleLoginSSO(encode(values));
  }, [location]);

  const completeAnimation = useSpring({ opacity: isAuthenticated ? 1 : 0 });
  const formAnimation = useSpring({
    opacity: isSSOLoginLoading || isLoginLoading || isAuthenticated ? 0 : 1,
    zIndex: 1,
  });
  const spinnerAnimation = useSpring({
    opacity: isSSOLoginLoading || isLoginLoading ? 1 : 0,
    zIndex: 0,
  });
  const theme = useTheme();

  const handleGoogleResponse = async (credentialResponse) => {
    const tokenId = credentialResponse.credential;

    if (!tokenId) {
      return;
    }

    const values = {
      client_id: process.env.CLIENT_ID,
      client_secret: process.env.CLIENT_SECRET,
      grant_type: 'google',
      scope: 'offline_access',
      tokenid: tokenId,
    };

    handleGoogleAuth(encode(values));
  };

  const handleSSOLogin = () => {
    const baseUrl = process.env.NODE_API_URL || `https://${process.env.ENVIRONMENT === 'PRODUCTION' ? '' : 'staging.'}api.teambuildr.com/`;
    window.location.replace(`${baseUrl}login/sso/${ssoData.ssoId}/`);
  };

  const showPasswordField = () => isNextButtonClicked
  && !isPreLoginLoading && (passwordButtonClicked || Object.keys(ssoData).length === 0);

  useEffect(() => {
    const handleResize = () => {
      setGoogleLoginWidth(window.innerWidth < 600 ? '270' : '322');
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <Formik
      initialValues={{
        client_id: process.env.CLIENT_ID,
        client_secret: process.env.CLIENT_SECRET,
        grant_type: 'password',
        password: '',
        scope: 'offline_access',
        username: '',
      }}
      onSubmit={(values, { setSubmitting }) => {
        setTimeout(() => {
          if (showPasswordField()) {
            handleLogin(encode(values));
            setSubmitting(false);
          }
        });
      }}
      render={({
        errors,
        handleSubmit,
        touched,
        values,
      }) => (
        <>
          <animated.div style={formAnimation}>
            <Form
              id='login'
              name='login'
            >
              {ssoLoginError && <SSOFieldError className='text-center'>Unable to login with your SSO provider. Please try again or contact support if the issue persists.</SSOFieldError>}
              {ssoLoginError && (
              <FormGroup className='form-group'>
                <Text styledTextProps={{ fontSize: theme.textFontSizes[0], fontWeight: '700' }} styledWrapperProps={{ marginBottom: '8px' }}>
                  Email Address
                </Text>
                <Field
                  className='form-control'
                  id='email-field'
                  name='username'
                  placeholder='Email'
                  type='email'
                />
                {errors.username && touched.username && <FieldError className='text-center'>{errors.username}</FieldError>}
              </FormGroup>
              )}
              {ssoLoginError && showPasswordField() ? (
                <FormGroup className='form-group'>
                  <Text styledTextProps={{ fontSize: theme.textFontSizes[0], fontWeight: '700' }} styledWrapperProps={{ marginBottom: '8px' }}>
                    Password
                  </Text>
                  <PasswordFieldWrapper>
                    <Field
                      autoComplete='off'
                      autoFocus
                      className='form-control'
                      name='password'
                      placeholder='Password'
                      type={showPassword ? 'text' : 'password'}
                    />
                    <PasswordToggleButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      tabIndex={-1}
                      type='button'
                    >
                      {showPassword ? 'Hide Password' : 'Show Password'}
                      <span style={{ marginLeft: '8px' }}>
                        {showPassword ? <HiEyeOff /> : <HiEye />}
                      </span>
                    </PasswordToggleButton>
                  </PasswordFieldWrapper>
                  {errors.password && touched.password && <FieldError className='text-center'>{errors.password}</FieldError>}
                  {loginError && <FieldError className='text-center'>Your email address or password is incorrect! Please try again.</FieldError>}
                </FormGroup>
              ) : null}
              {ssoLoginError && (
              <FormGroup className='form-group'>
                {googleError && <FieldError className='text-center'>This google account does not seem to be associated with a TeamBuildr account.</FieldError>}
              </FormGroup>
              )}
              {ssoLoginError
              && (
              <FormGroup className='text-center'>
                {(!isNextButtonClicked || isPreLoginLoading) && (
                <Button
                  bgColor={theme.colors.orange}
                  fontSize='14px'
                  fullWidth
                  isLoading={!showPasswordField() && isPreLoginLoading}
                  large
                  lightSpinner
                  name='Next'
                  noBorder
                  onClick={() => {
                    setIsNextButtonClicked(true);
                    // username is the email
                    handlePreLogin(values.username);
                  }}
                  rounded
                  styledWrapperProps={{ margin: '25px 0px 20px' }}
                  txtColor={theme.colors.white}
                  type='submit'
                  upperCase
                />
                )}
                {Object.keys(ssoData).length > 0 && !passwordButtonClicked ? (
                  <Button
                    bgColor={theme.colors.lightBlue}
                    disabled={false}
                    fontSize='14px'
                    fullWidth
                    large
                    name={`Sign in with ${ssoData.loginName}`}
                    noBorder
                    onClick={handleSSOLogin}
                    rounded
                    styledWrapperProps={{ marginBottom: '10px' }}
                    txtColor={theme.colors.white}
                    upperCase
                  />
                ) : null}
                {Object.keys(ssoData).length > 0 && !passwordButtonClicked ? (
                  <DividerContainer>
                    <div className='divider-line' />
                    <Text styledTextProps={{ fontWeight: '700' }}>OR</Text>
                    <div className='divider-line' />
                  </DividerContainer>
                ) : null}
                {isNextButtonClicked
                && !passwordButtonClicked && Object.keys(ssoData).length > 0 ? (
                  <Button
                    bgColor={theme.colors.orange}
                    fontSize='14px'
                    fullWidth
                    large
                    name='Sign In With Password'
                    noBorder
                    onClick={() => setPasswordButtonClicked(true)}
                    rounded
                    styledWrapperProps={{ marginBottom: '20px' }}
                    txtColor={theme.colors.white}
                    upperCase
                  />
                  ) : null}
                {showPasswordField() ? (
                  <Button
                    bgColor={theme.colors.orange}
                    disabled={errors.password
                      || errors.username || (showPasswordField() && !values.password)}
                    fontSize='14px'
                    fullWidth
                    isLoading={isLoginLoading}
                    large
                    lightSpinner
                    name='Sign In'
                    noBorder
                    onClick={handleSubmit}
                    rounded
                    styledWrapperProps={{ marginBottom: '20px' }}
                    txtColor={theme.colors.white}
                    upperCase
                  />
                ) : null}
                <GoogleLogin
                  locale='en'
                  onSuccess={handleGoogleResponse}
                  text='signin_with'
                  width={googleLoginWidth}
                />
                {flags.registration ? (
                  <Link allCaps className='easy-join' color='lightText.1' href='/signup/easy-join'>
                    Have an Easy Join Code? Click Here
                  </Link>
                ) : (
                  <Link allCaps className='easy-join' color='lightText.1' href='https://app.teambuildr.com/signup?ej=true'>
                    Have an Easy Join Code? Click Here
                  </Link>
                )}
                <Link allCaps color='lightText.2' href='/forgot'>
                  Forgot Your Password?
                </Link>
              </FormGroup>
              )}

            </Form>
          </animated.div>
          <animated.div style={spinnerAnimation}>
            <SpinnerContainer>
              <Spinner />
            </SpinnerContainer>
          </animated.div>
          <animated.div style={completeAnimation}>
            <SpinnerContainer>
              <Checkmark />
            </SpinnerContainer>
          </animated.div>
        </>
      )}
      validationSchema={showPasswordField() ? PasswordSchema : EmailSchema}
    />
  );
};

LoginFormSSO.propTypes = {
  flags: PropTypes.shape({
    registration: PropTypes.bool.isRequired,
  }).isRequired,
};

export default withLDConsumer()(LoginFormSSO);
